import { FC, useState } from "react";
import { flushSync } from "react-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { UserPlus, X } from "lucide-react";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import useWorkspace from "@/hooks/useWorkspace";
import Workspace from "@/models/workspace/Workspace";
import { UserWithRole } from "@/models/users/User";
import { logger } from "@/lib/logger";
import {
  formatBulkClaimPartialFailureMessage,
  formatBulkClaimSuccessMessage,
  formatBulkClaimTotalFailureMessage,
} from "../utils/orphanedWorkspacesSelection";
import OwnerPicker from "./OwnerPicker";

const MAX_LISTED_WORKSPACE_NAMES = 5;

interface BulkClaimOrphanedWorkspacesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaces: Workspace[];
  onClaimSuccess: () => void;
  onPartialClaim: (succeededIds: string[]) => void;
}

const BulkClaimOrphanedWorkspacesDialog: FC<
  BulkClaimOrphanedWorkspacesDialogProps
> = ({ isOpen, onClose, workspaces, onClaimSuccess, onPartialClaim }) => {
  const { claimWorkspaceOwnership, invalidateWorkspaceQueries } =
    useWorkspace();
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);

  if (!isOpen || workspaces.length === 0) return null;

  const workspaceCount = workspaces.length;
  const listedWorkspaces = workspaces.slice(0, MAX_LISTED_WORKSPACE_NAMES);
  const remainingNameCount = workspaceCount - listedWorkspaces.length;
  const isPending = isClaiming;

  const handleClose = () => {
    setSelectedUser(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedUser) return;

    const totalCount = workspaces.length;
    setIsClaiming(true);

    try {
      const results = await Promise.allSettled(
        workspaces.map((workspace) =>
          claimWorkspaceOwnership(workspace.id, selectedUser.id).then(
            () => workspace.id
          )
        )
      );

      const succeededIds = results
        .filter(
          (result): result is PromiseFulfilledResult<string> =>
            result.status === "fulfilled"
        )
        .map((result) => result.value);

      const successCount = succeededIds.length;

      const firstFailure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (firstFailure) {
        logger.error("Failed to claim orphaned workspaces", {
          operation: "bulkClaimOrphanedWorkspaces",
          error: firstFailure.reason,
          newOwnerId: selectedUser.id,
          workspaceCount: totalCount,
          failedCount: totalCount - successCount,
        });
      }

      if (successCount > 0) {
        invalidateWorkspaceQueries();
      }

      if (successCount === totalCount) {
        flushSync(() => {
          onClaimSuccess();
        });
        showSuccessToast(
          formatBulkClaimSuccessMessage(successCount),
          "Tildelt"
        );
        handleClose();
      } else if (successCount > 0) {
        flushSync(() => {
          onPartialClaim(succeededIds);
        });
        showErrorToast(
          formatBulkClaimPartialFailureMessage(successCount, totalCount),
          "Delvis tildeling"
        );
        handleClose();
      } else {
        showErrorToast(formatBulkClaimTotalFailureMessage(), "Fejl");
      }
    } catch (error) {
      logger.error("Failed to claim orphaned workspaces", {
        operation: "bulkClaimOrphanedWorkspaces",
        error,
        newOwnerId: selectedUser.id,
        workspaceCount: totalCount,
      });
      showErrorToast(formatBulkClaimTotalFailureMessage(), "Fejl");
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tildel ejer til valgte workspaces</DialogTitle>
          <DialogDescription>
            Vælg en bruger som skal blive ejer af {workspaceCount}{" "}
            {workspaceCount === 1 ? "workspace" : "workspaces"}.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">Følgende workspaces tildeles:</p>
            <ul className="list-disc list-inside max-h-24 overflow-y-auto">
              {listedWorkspaces.map((workspace) => (
                <li key={workspace.id} className="truncate">
                  {workspace.name}
                </li>
              ))}
              {remainingNameCount > 0 && (
                <li className="text-muted-foreground">
                  + {remainingNameCount} flere
                </li>
              )}
            </ul>
          </div>

          <OwnerPicker
            selectedUser={selectedUser}
            onSelectUser={setSelectedUser}
            disabled={isPending}
            isActive={isOpen}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            <X className="w-4 h-4 mr-1" />
            Annuller
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedUser || isPending}>
            {isPending ? (
              <Spinner className="text-primary-foreground" />
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-1" />
                Tildel ejer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkClaimOrphanedWorkspacesDialog;
